<template>
  <q-card class="invoice-card" :style="{ 'border-color': statusColor }">
    <q-card-title class="invoice-card__title-wrapper">
      <span class="invoice-card__status" :style="{ 'background-color': statusColor }">{{ $t(invoice.status) }}</span>
      <span class="invoice-card__title">
         <router-link
           v-if="invoice.status !== 'draft'"
           :to="{ name: 'invoice',  params: { invoice: invoice.id, teamId: activeTeam.id } }"
           class="router-link"
         >
           {{ $tc('invoice', 1) }}
           <template v-if="invoice.invoice_number">&nbsp;<strong>#{{ invoice.invoice_number }}</strong></template>
          </router-link>

          <template v-else>
            {{ $tc('invoice', 1) }}
            <template v-if="invoice.invoice_number">&nbsp;<strong>#{{ invoice.invoice_number }}</strong></template>
          </template>
      </span>
    </q-card-title>

    <q-card-main class="invoice-card__main">
      <div class="invoice-card__meta">
        <div class="invoice-card__entry">
          <h5 class="invoice-card__entry-heading">{{ $tc('customer', 1) }}</h5>
          <span class="invoice-card__entry-value">
            <router-link :to="{ name: 'customer', params: { customerId: customer.id, teamId: this.activeTeam.id } }"
                         class="router-link">
              {{ customerName }}
            </router-link>
          </span>
        </div>

        <div class="invoice-card__entry">
          <h5 class="invoice-card__entry-heading">{{ $t('invoice_date') }}</h5>
          <span class="invoice-card__entry-value">{{ invoiceDate }}</span>
        </div>
      </div>

      <div class="invoice-card__row invoice-card__row--bottom">
        <div class="invoice-card__entry invoice-card__entry--center">
          <h5 class="invoice-card__entry-heading">{{ $t('sum_gross') }}</h5>
          <span class="invoice-card__entry-value invoice-card__entry-value--big">{{ totalGross }}</span>
        </div>
      </div>
    </q-card-main>

    <q-card-actions class="invoice-card__actions">
      <div class="col">
        <q-btn v-if="!isDraft" @click="handleView" flat dense icon="visibility"/>
        <q-btn v-if="!isCommitted" @click="handleEdit" flat dense icon="edit"/>
        <q-btn v-if="isDraft" @click="handleRemove" flat dense icon="delete"/>
        <q-btn v-if="!isDraft" @click="handlePdfDownload" flat dense icon="cloud_download"/>
      </div>

      <div class="col">
        <div class="row justify-end">
          <q-checkbox
            v-if="selectable"
            v-model="selected"
            @input="$emit('select', $event)"
            :class="['invoice-card__actions__select', { 'block': selected }]"
          />
        </div>
      </div>
    </q-card-actions>
  </q-card>
</template>

<script>
  import { colors, date } from 'quasar'
  import { mapActions, mapGetters } from 'vuex'
  import FileSaver from 'file-saver'
  import { InvoiceClient } from 'src/api/Invoices'
  import { formatMoney } from 'src/utils/format'
  import { getCustomerName } from 'src/utils/searchResultParser'
  import { invoices } from 'src/config'
  import parseDate from 'date-fns/parse'

  export default {
    props: {
      invoice: {
        required: true,
        type: Object
      },

      selectable: {
        type: Boolean,
        default: false
      }
    },

    data () {
      return {
        selected: false
      }
    },

    computed: {
      ...mapGetters(['activeTeam']),

      statusColor () {
        return colors.getBrand(invoices.status_colors[this.invoice.status])
      },

      totalGross () {
        return formatMoney(this.invoice.total_gross, this.invoice.currency)
      },

      customer () {
        return this.invoice.customer.data
      },

      customerName () {
        return getCustomerName(this.customer)
      },

      invoiceDate () {
        return date.formatDate(parseDate(this.invoice.invoice_date.date), this.$t('date_format'))
      },

      isDraft () {
        return this.invoice.status === 'draft'
      },

      isCommitted () {
        return this.invoice.committed_at !== null
      }
    },

    methods: {
      ...mapActions(['deleteInvoices']),

      handleView () {
        this.$router.push({ name: 'invoice', params: { team: this.activeTeam.id, invoice: this.invoice.id } })
      },

      handleEdit () {
        this.$router.push({ name: 'edit-invoice', params: { team: this.activeTeam.id, invoice: this.invoice.id } })
      },

      handleRemove () {
        this.$q.dialog({
          title: this.$tc('confirm_checked_invoices_delete_title', 1),
          message: this.$tc('confirm_checked_invoices_delete_body', 1),
          ok: this.$t('confirm'),
          cancel: this.$t('abort')
        })
          .then(() => this.deleteInvoices([this.invoice]))
          // no-op
          .catch(() => {})
      },

      handlePdfDownload () {
        new InvoiceClient()
          .pdf(this.invoice.id)
          .then(response => {
            const blob = new Blob([response.data], { type: 'application/pdf' })
            FileSaver.saveAs(blob, this.$tc('invoice', 1) + '_' + this.invoice.invoice_number + '.pdf')
          })
      }
    }
  }
</script>

<style lang="stylus" src="./invoice-card.styl"></style>
